import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getMessagingFailures, type MessagingFailure } from "../lib/api.ts";

// Compact warning rendered in the layout when one or more email/SMS
// sends have failed in the last 24 hours. Click to expand the last 5
// failure messages; "Open Settings → Messaging" jumps to the config.

export function MessagingFailuresBanner({ slug }: { slug: string }) {
  const [count, setCount] = useState(0);
  const [recent, setRecent] = useState<MessagingFailure[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      getMessagingFailures(slug)
        .then((r) => {
          if (cancelled) return;
          setCount(r.recent_count);
          setRecent(r.recent);
        })
        .catch(() => { /* silent — banner just won't show */ });
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000); // re-check every 5 min
    return () => { cancelled = true; clearInterval(t); };
  }, [slug]);

  if (count === 0) return null;

  return (
    <div
      style={{
        margin: "12px 0 16px",
        padding: 0,
        background: "var(--status-cancelled-bg)",
        border: "1px solid var(--status-cancelled)",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          padding: "10px 14px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 10,
          fontSize: 13,
          color: "var(--ink)",
          fontFamily: "inherit",
        }}
        aria-expanded={open}
      >
        <span style={{ fontWeight: 600 }}>
          {count} customer message{count === 1 ? "" : "s"} failed in the last 24h.
        </span>
        <span style={{ flex: 1 }} />
        <Link
          to={`/${slug}/settings`}
          style={{ fontSize: 12, color: "var(--ink)", textDecoration: "underline" }}
          onClick={(e) => e.stopPropagation()}
        >
          Open Settings → Messaging
        </Link>
        <span style={{ fontSize: 11, color: "var(--ink-faint)" }}>{open ? "▴" : "▾"}</span>
      </button>

      {open && (
        <div style={{ padding: "0 14px 12px", borderTop: "1px solid var(--rule)" }}>
          <ul style={{ listStyle: "none", padding: 0, margin: "10px 0 0" }}>
            {recent.map((f) => (
              <li key={f.id} style={{ padding: "8px 0", borderBottom: "1px solid var(--rule)", fontSize: 12 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                      fontSize: 10,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "var(--ink-faint)",
                    }}
                  >
                    {f.channel} · {f.provider}
                  </span>
                  <span style={{ color: "var(--ink)" }}>{f.to_target}</span>
                  <span style={{ color: "var(--ink-faint)", marginLeft: "auto" }}>
                    {new Date(f.created_at + "Z").toLocaleString()}
                  </span>
                </div>
                <div style={{ marginTop: 4, color: "var(--ink-soft)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {f.error}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
